<script setup>
import { RouterLink, useRoute } from 'vue-router'
import { useStateStore } from '@/stores/state.store'
import { useShopify } from '@/stores/shopify'
import MainFooter from './MainFooter.vue'
import IconHelp from './icons/IconHelp.vue'
import IconVideoPlayer from './icons/IconVideoPlayer.vue'
import IconLogout from './icons/IconLogout.vue'
import IconGift from './icons/IconGift.vue'
import SideSheet from './SideSheet.vue'
import RoomCard from './RoomCard.vue'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import LoadingIndicator from './UI/LoadingIndicator.vue'
import { storeToRefs } from 'pinia'

const influencers = ref([])
const _loading = ref(false)

const open = defineModel({
  type: Boolean,
  default: false
})

const shopList = ref()

const state = useStateStore()
const shopify = useShopify()
const { birdsEyeView } = storeToRefs(state)

const showTutorial = () => {
  state.tutorialShown = false
}

onMounted(() => {
  shopList.value.addEventListener('mousedown', mouseDown)
  shopList.value.addEventListener('mousemove', mouseMove)
  shopList.value.addEventListener('mouseup', dragEnd)
  shopList.value.addEventListener('mouseleave', dragEnd)
})

onBeforeUnmount(() => {
  shopList.value.removeEventListener('mousedown', mouseDown)
  shopList.value.removeEventListener('mousemove', mouseMove)
  shopList.value.removeEventListener('mouseup', dragEnd)
  shopList.value.removeEventListener('mouseleave', dragEnd)
})

_loading.value = true
shopify
  .fetchInfluencers(false, 128, 128)
  .then((response) => {
    influencers.value = response
  })
  .finally(() => {
    _loading.value = false
  })

const route = useRoute()

let drag = ref(false)
let startX = 0
let diffX = 0

const dragEnd = () => {
  if (drag.value) {
    drag.value = false

    let start = 1,
      animate = function () {
        let step = Math.sin(start)
        if (step <= 0) {
          window.cancelAnimationFrame(animate)
        } else {
          shopList.value.scrollLeft += diffX * step
          start -= 0.02
          window.requestAnimationFrame(animate)
        }
      }
    animate()
  }
}

const mouseDown = (event) => {
  startX = event.clientX + shopList.value.scrollLeft
  diffX = 0
  event.preventDefault()
}

const mouseMove = (event) => {
  if (event.buttons === 1 && event.button === 0) {
    diffX = startX - (event.clientX + shopList.value.scrollLeft)
    shopList.value.scrollLeft += diffX
    drag.value = true
  }
}
</script>

<template>
  <SideSheet title="Menü" header-alignment="left" v-model="open" icon-only>
    <div class="container">
      <nav>
        <div class="navigation__shops">
          <h4 class="headline--h4">Mehr Räume</h4>
          <div class="navigation__shops-list" ref="shopList">
            <RoomCard
              v-for="room in state.roomStack.slice().reverse()"
              :key="room.handle"
              :open="true"
              :roomHandle="room.handle"
              :disabled="drag"
              :bgImage="room.background"
              :bgColor="room.color"
              :avatar="room.image"
              :title="room.title"
              type="navigation"
              :current="route.params.room_id === room.handle"
            />
            <LoadingIndicator v-if="_loading" />
            <template v-for="influencer in influencers" :key="influencer.node.id">
              <RoomCard
                v-if="
                  state.roomStack.findIndex((elem) => elem.handle === influencer.node.handle) === -1
                "
                :roomHandle="influencer.node.handle"
                :open="influencer.node.open.value == 'true'"
                :disabled="drag"
                :key="influencer.node.handle"
                :bgImage="influencer.node.backdrop_image.reference.previewImage.url"
                :bgColor="influencer.node.color?.value"
                :avatar="influencer.node.image.url"
                :title="influencer.node.title"
                type="navigation"
              />
            </template>
          </div>
        </div>

        <div class="navigation__list">
          <RouterLink :to="{ name: 'ProductListView' }"><IconGift />Produkte</RouterLink>
          <RouterLink :to="{ name: 'HelpView' }"><IconHelp />Hilfe</RouterLink>
          <a href="#" v-if="route.name == 'RoomView' && birdsEyeView" @click.prevent="showTutorial"><IconVideoPlayer />Anleitungen ansehen</a>
          <RouterLink to="/"><IconLogout />Zurück zur Lobby</RouterLink>
        </div>
      </nav>
    </div>
    <MainFooter class="navigation__footer" />
  </SideSheet>
</template>

<style lang="scss" scoped>
.container {
  padding: 0 24px;
}
.navigation {

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  .headline--h3 {
    font-size: 24px;
  }

  .headline--h4 {
    font-size: 20px;
  }

  &__shops {
    margin-block: 16px;
  }

  &__shops-list {
    display: flex;
    gap: 4px;
    margin: 24px -24px 0 -24px;
    padding: 2px 20px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    margin-bottom: 25px;

    a {
      display: flex;
      align-items: center;
      padding: 24px 12px;
      font-size: 18px;
      color: #242635;
      gap: 12px;
      text-decoration: none;
      border-bottom: 1px solid #f0f0f0;

      &:last-of-type {
        border-bottom: none;
      }
    }
  }

  &__bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;

    &--logo {
      max-width: 42px;
    }

    a {
      font-size: 12px;
      color: #242635;
      text-decoration: none;
    }
  }
}
</style>
